import React, { useEffect, useRef, useState } from 'react';
import './InhibitRestartButton.less';
import { Telematics } from 'types/IEquipmentEntry';
import useFlag from 'app/hooks/useFlag';
import { FeatureName } from 'types/entities/MobileSettings';
import { Trans, useTranslation } from 'react-i18next';
import clsx from 'clsx';
import RestartInhibitDefaultIcon from 'assets/images/restart_inhibit_default.svg';
import { Switch } from '@progress/kendo-react-inputs';
import { InhibitRestart } from 'app/pages/InhibitRestartPage';
import { useNotificationsContext } from 'context';
import { WarningNotification } from 'app/components/Notifications/WarningNotification';
import { SubscriptionInfo } from 'app/hooks/subscriptions/useSubscriptionInfo';
import { BannerType } from 'utils/banner';
import useToggleLogic from 'app/pages/Equipment/EquipmentContent/components/InhibitRestartButton/useToggleLogic';
import { InhibitRestartStatus } from 'app/pages/InhibitRestartPage/type';

interface Props {
  equipmentId: string;
  telematics?: Telematics;
  hasAccess: boolean;
  subscriptionInfo?: SubscriptionInfo;
}

export default function InhibitRestartButton({
  equipmentId,
  telematics,
  hasAccess,
  subscriptionInfo,
}: Props) {
  const { t } = useTranslation();
  const isSubscriptionsFlagEnabled = useFlag(FeatureName.SUBSCRIPTIONS);
  const { addNotification, addSnackbar, removeNotification } =
    useNotificationsContext();
  const notificationRef = useRef<string | null>(null);

  const [
    isInhibitRestartDialogVisibility,
    setIsInhibitRestartDialogVisibility,
  ] = useState(false);

  const { canModify, commandStatus, equipmentFeatureStatus, equipmentStatus } =
    telematics?.restartInhibitStatus ?? {};

  const hasPremiumSubscription = subscriptionInfo?.hasPremium ?? false;
  const isBasicSubscription =
    isSubscriptionsFlagEnabled && !hasPremiumSubscription;

  const isUnlockFeatureTextVisible =
    isBasicSubscription &&
    (equipmentFeatureStatus === InhibitRestartStatus.Disabled ||
      (equipmentFeatureStatus === InhibitRestartStatus.Enabled &&
        equipmentStatus === InhibitRestartStatus.RestartEnabled &&
        commandStatus === InhibitRestartStatus.RestartEnabled));

  const { isChecked, isDisabled, notificationType } = useToggleLogic({
    hasAccess,
    canModify,
    isSubscriptionsFlagEnabled,
    hasPremiumSubscription,
    equipmentFeatureStatus,
    equipmentStatus,
    commandStatus,
  });

  useEffect(() => {
    return () => {
      if (notificationRef.current) {
        removeNotification(notificationRef.current);
        notificationRef.current = null;
      }
    };
  }, []);

  function handleSwitchChange() {
    if (notificationType === 'feature-available-in-premium') {
      const notificationId = addNotification(
        <WarningNotification
          title={{ i18nKey: 'subscriptions.notifications.more_features.title' }}
          subtitle={{
            i18nKey: 'subscriptions.notifications.more_features.subtitle',
          }}
        />,
        'content-centered',
        3000,
        null,
        `${BannerType.SUBSCRIPTION_UNLOCK_FEATURES}-${equipmentId}`,
      );
      notificationRef.current = notificationId;
    } else if (notificationType === 'you-do-not-have-access') {
      addSnackbar(
        <div className="snackbar-container">
          <span>{t('subscriptions.notifications.you_do_not_have_access')}</span>
        </div>,
      );
    } else {
      setIsInhibitRestartDialogVisibility(true);
    }
  }

  return (
    <>
      {isInhibitRestartDialogVisibility && (
        <InhibitRestart
          onClose={() => {
            setIsInhibitRestartDialogVisibility(false);
          }}
        />
      )}

      <div
        className={clsx('inhibit-restart-button', {
          'inhibit-restart-button_premium': hasPremiumSubscription,
        })}
      >
        <img
          src={RestartInhibitDefaultIcon}
          alt="Inhibit Restart"
          className="inhibit-restart-button__icon"
        />

        <div className="inhibit-restart-button__text-block ">
          <span className="inhibit-restart-button__title">
            {t('kubota_now_inhibit_restart')}
          </span>

          {isUnlockFeatureTextVisible && (
            <p className="inhibit-restart-button__basic-sub-subtitle">
              <Trans i18nKey="unlock_this_feature_with" />
            </p>
          )}
        </div>

        <Switch
          className={clsx('inhibit-restart-button__switch', {
            'inhibit-restart-button__switch_disabled': isDisabled,
          })}
          checked={isChecked}
          onLabel=""
          offLabel=""
          onChange={handleSwitchChange}
        />
      </div>
    </>
  );
}
